// ============================================================
// exams-teacher.jsx — Exact HTML design + original data logic
// ============================================================

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './exams-teacher.css';
import {
  useExams,
  toNumber,
  calculatePassThreshold,
  sanitizeHtml,
  isGlobalAdmin,
  GLOBAL_ADMINS,
  getGlobalExams,
  getGlobalExamStats,
  idsEqual,
} from './ExamContext.jsx';
import { getField as getFieldMeta } from '../../core/education/curriculum.js';
import { useAuth } from '../../context/AuthContext.jsx';

/* ============ Helpers ============ */
const getUserDisplayName = (u) => {
  if (!u) return 'معلم';
  if (u.firstName && u.lastName) return `${u.firstName} ${u.lastName}`.trim();
  if (u.firstName) return u.firstName;
  if (u.lastName) return u.lastName;
  if (u.name) return u.name;
  if (u.fullName) return u.fullName;
  if (u.username) return u.username;
  return 'معلم';
};
const classNames = (...c) => c.filter(Boolean).join(' ');
const formatGradeWithField = (exam) => {
  if (!exam) return '';
  const g = exam.grade || '';
  if (!exam.field) return g;
  const fm = getFieldMeta(exam.field);
  const f = fm?.shortTitle || fm?.title || '';
  return f ? `${g} (${f})` : g;
};

/* ============ Inline SVG icons (matching HTML) ============ */
const I = {
  file: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
    </svg>
  ),
  check: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
    </svg>
  ),
  clock: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
    </svg>
  ),
  barChart: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
    </svg>
  ),
  award: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="7"/><polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"/>
    </svg>
  ),
  globe: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
    </svg>
  ),
  trophy: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/><path d="M4 22h16"/><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/>
    </svg>
  ),
  sparkles: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
    </svg>
  ),
  pencil: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
    </svg>
  ),
  users: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
    </svg>
  ),
  settings: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/>
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
    </svg>
  ),
  sun: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
      <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
    </svg>
  ),
  moon: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
    </svg>
  ),
  edit: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
  ),
  copy: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="9" y="9" width="13" height="13" rx="2"/>
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
    </svg>
  ),
  results: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
    </svg>
  ),
  archive: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/>
    </svg>
  ),
  trash: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="3 6 5 6 21 6"/>
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
    </svg>
  ),
  restore: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>
    </svg>
  ),
  filter: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
    </svg>
  ),
  rocket: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/>
      <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/>
      <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/>
      <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/>
    </svg>
  ),
};

const ExamsTeacher = () => {
  const navigate = useNavigate();
  const {
    exams, deleteExam, duplicateExam, updateExam,
    getPendingCorrections, getExamsByTeacher, getExamStats,
    archiveExam, restoreExam, getExam, getTeamTotalStudents,
    processCorrection, getStudentStatsForExam,
    getGlobalExams: getGlobalExamsFromContext,
    getGlobalExamStats: getGlobalExamStatsFromContext,
    isExamOpenForEntry,
  } = useExams();

  /* ============ getExamDisplayStatus (unchanged) ============ */
  const getExamDisplayStatus = useCallback((exam) => {
    if (!exam) return { label: 'نامشخص', type: 'unknown' };
    if (exam.statusType === 'archived') return { label: 'بایگانی شده', type: 'archived' };
    if (exam.statusType === 'pending') return { label: 'در انتظار انتشار', type: 'pending' };
    if (!exam.startTime || !exam.endTime) {
      return { label: exam.status || 'نامشخص', type: exam.statusType || 'unknown' };
    }
    const isOpen = isExamOpenForEntry(exam);
    if (isOpen) return { label: 'فعال', type: 'active' };
    const now = new Date();
    let start = new Date(exam.startTime);
    if (isNaN(start.getTime()) && exam.date) start = new Date(`${exam.date}T${exam.startTime}:00`);
    if (start && now < start) return { label: 'در انتظار شروع', type: 'pending' };
    return { label: 'بسته شده', type: 'closed' };
  }, [isExamOpenForEntry]);

  /* ============ State ============ */
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('همه');
  const [showGlobalManagement, setShowGlobalManagement] = useState(false);
  const [showChallengeManagement, setShowChallengeManagement] = useState(false);

  // 🆕 فیلترهای دسته‌بندی «آزمون‌های کلاس من»
  const [showMyExamsFilters, setShowMyExamsFilters] = useState(false);
  const [myGradeFilter, setMyGradeFilter] = useState('');
  const [myBookFilter, setMyBookFilter] = useState('');
  const [myChapterFilter, setMyChapterFilter] = useState('');
  const [activeTab, setActiveTab] = useState('my-exams');
  const [darkMode, setDarkMode] = useState(() => {
    try { return localStorage.getItem('navbar2-drawer-dark') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('navbar2-drawer-dark', darkMode ? '1' : '0'); } catch {}
  }, [darkMode]);
  useEffect(() => {
    const handleStorage = (e) => { if (e.key === 'navbar2-drawer-dark') setDarkMode(e.newValue === '1'); };
    window.addEventListener('storage', handleStorage);
    const interval = setInterval(() => {
      try {
        const cur = localStorage.getItem('navbar2-drawer-dark') === '1';
        setDarkMode((prev) => (prev !== cur ? cur : prev));
      } catch {}
    }, 500);
    return () => { window.removeEventListener('storage', handleStorage); clearInterval(interval); };
  }, []);

  const [user, setUser] = useState(null);
  const [teacherName, setTeacherName] = useState('معلم');
  const [teacherId, setTeacherId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const { user: authUser } = useAuth();
  useEffect(() => {
    if (authUser) {
      setUser(authUser);
      setTeacherName(getUserDisplayName(authUser));
      setTeacherId(authUser?.id || null);
      setIsAdmin(authUser.role === 'admin' || authUser.role === 'superadmin');
    }
  }, [authUser]);

  const [teamMembers, setTeamMembers] = useState([]);
  const [users, setUsers] = useState([]);
  const teamMembersRef = useRef([]);
  const loadTeamMembers = useCallback(() => {
    try {
      const storedMembers = localStorage.getItem('teamMembers');
      if (storedMembers) {
        const parsed = JSON.parse(storedMembers);
        const activeMembers = parsed.filter((m) => m.status === 'active');
        setTeamMembers(activeMembers);
        teamMembersRef.current = activeMembers;
      }
    } catch (e) { console.error('Error loading team members:', e); }
    try {
      if (typeof window !== 'undefined' && Array.isArray(window.__allUsersCache)) setUsers(window.__allUsersCache);
    } catch (e) { console.error('Error loading users:', e); }
  }, []);
  useEffect(() => {
    loadTeamMembers();
    const h = (e) => { if (e.key === 'teamMembers' || e.key === 'users') loadTeamMembers(); };
    window.addEventListener('storage', h);
    return () => window.removeEventListener('storage', h);
  }, [loadTeamMembers]);

  const getStudentName = useCallback((studentId) => {
    if (!studentId) return 'دانش‌آموز';
    const m = teamMembers.find((x) => idsEqual(x.studentId, studentId));
    if (m?.name) return m.name;
    const f = users.find((u) => idsEqual(u.id, studentId));
    if (f?.name) return getUserDisplayName(f);
    if (user?.id === studentId && user?.name) return user.name;
    return `دانش‌آموز ${String(studentId).slice(0, 8)}`;
  }, [teamMembers, users, user]);

  /* ============ Memos ============ */
  const myExams = useMemo(() => (teacherId ? getExamsByTeacher(teacherId) : []), [teacherId, getExamsByTeacher]);
  const globalExams = useMemo(() => getGlobalExamsFromContext(), [getGlobalExamsFromContext]);
  const globalStats = useMemo(() => getGlobalExamStatsFromContext(), [getGlobalExamStatsFromContext]);
  const challengeExams = useMemo(
    () => exams.filter((e) => e.isChallenge === true && e.statusType !== 'archived'),
    [exams]
  );
  const challengeStats = useMemo(() => ({
    total: challengeExams.length,
    active: challengeExams.filter((e) => e.statusType === 'active').length,
    correction: challengeExams.filter((e) => e.statusType === 'correction').length,
  }), [challengeExams]);

  /* ============ Modal states ============ */
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedExamId, setSelectedExamId] = useState(null);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [showCorrectionModal, setShowCorrectionModal] = useState(false);
  const [selectedExamForResults, setSelectedExamForResults] = useState(null);
  const [selectedExamForCorrection, setSelectedExamForCorrection] = useState(null);
  const [correctionScores, setCorrectionScores] = useState({});
  const [currentCorrectionIndex, setCurrentCorrectionIndex] = useState(0);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [selectedExamForArchive, setSelectedExamForArchive] = useState(null);
  const [isSavingCorrection, setIsSavingCorrection] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [selectedExamForStatus, setSelectedExamForStatus] = useState(null);
  const [correctionError, setCorrectionError] = useState(null);

  // 🆕 Bottom sheet انتخاب نوع آزمون (فقط ادمین)
  const [createTypeSheet, setCreateTypeSheet] = useState(null);
  // مقدار: null | 'group' | 'public' | 'challenge'

  // 🆕 Management Modal — فیلتر حرفه‌ای آزمون‌های عمومی و چالش
  const [managementModalOpen, setManagementModalOpen] = useState(null);
  // مقدار: null | 'global' | 'challenge'
  const [mgmtGrade, setMgmtGrade] = useState('');
  const [mgmtField, setMgmtField] = useState('');
  const [mgmtBook, setMgmtBook] = useState('');
  const [mgmtChapter, setMgmtChapter] = useState('');
  const [mgmtStatusFilter, setMgmtStatusFilter] = useState('همه');
  const [mgmtSearchTerm, setMgmtSearchTerm] = useState('');
  const [mgmtVisibleCount, setMgmtVisibleCount] = useState(24);

  /* ============ Reports ============ */
  const reports = useMemo(() => {
    const stats = getExamStats();
    let totalScores = 0, totalStudents = 0, passCount = 0;
    myExams.forEach((exam) => {
      const results = exam.results || [];
      if (results.length > 0) {
        results.forEach((r) => {
          totalScores += toNumber(r.score);
          totalStudents++;
          const maxScore = exam.questions?.reduce((s, q) => s + toNumber(q.score, 1), 0) || 0;
          if (toNumber(r.score) >= calculatePassThreshold(maxScore)) passCount++;
        });
      }
    });
    const avgClass = totalStudents > 0 ? (totalScores / totalStudents).toFixed(1) : '--';
    const passRate = totalStudents > 0 ? Math.round((passCount / totalStudents) * 100) : 0;
    const arr = [
      { label: 'میانگین کلاس', value: avgClass, sub: 'بر اساس نتایج ثبت‌شده', icon: I.barChart, tone: 'brand' },
      { label: 'نرخ قبولی', value: `${passRate}%`, sub: 'نسبت به کل شرکت‌کنندگان', icon: I.check, tone: 'success' },
      { label: 'تعداد آزمون‌ها', value: stats.total || 0, sub: 'در این ترم', icon: I.file, tone: 'brand' },
      { label: 'در انتظار تصحیح', value: getPendingCorrections() || 0, sub: 'پاسخ‌های تشریحی', icon: I.clock, tone: 'warning' },
    ];
    if (isAdmin) {
      arr.push({ label: 'آزمون‌های عمومی', value: globalStats.total || 0, sub: `${globalStats.active} فعال`, icon: I.globe, tone: 'brand' });
      arr.push({ label: 'چالش‌ها', value: challengeStats.total || 0, sub: `${challengeStats.active} فعال`, icon: I.trophy, tone: 'warning' });
    }
    return arr;
  }, [myExams, getExamStats, getPendingCorrections, isAdmin, globalStats, challengeStats]);

  /* ============ KPIs ============ */
  const activeExamsCount = useMemo(
    () => myExams.filter((e) => getExamDisplayStatus(e).type === 'active').length,
    [myExams, getExamDisplayStatus]
  );
  const pendingCount = useMemo(() => getPendingCorrections() || 0, [getPendingCorrections, exams]);

  const kpiData = useMemo(() => {
    const total = getExamStats().total || 0;
    const avg = reports.find((r) => r.label === 'میانگین کلاس')?.value ?? '--';
    const pass = reports.find((r) => r.label === 'نرخ قبولی')?.value ?? '0%';
    const items = [
      { icon: I.file, value: total, label: 'کل آزمون‌ها', tone: 'brand' },
      { icon: I.check, value: activeExamsCount, label: 'آزمون‌های فعال', tone: 'success' },
      { icon: I.clock, value: pendingCount, label: 'در انتظار تصحیح', tone: 'warning' },
      { icon: I.barChart, value: avg, label: 'میانگین کلاس', tone: 'brand' },
      { icon: I.award, value: pass, label: 'نرخ قبولی', tone: 'success' },
    ];
    if (isAdmin) {
      items.push({ icon: I.globe, value: globalStats.total || 0, label: 'آزمون‌های عمومی', tone: 'brand' });
      items.push({ icon: I.trophy, value: challengeStats.total || 0, label: 'چالش‌ها', tone: 'warning' });
    }
    return items;
  }, [getExamStats, reports, activeExamsCount, pendingCount, isAdmin, globalStats, challengeStats]);

  /* ============ Chart ============ */
  const chartData = useMemo(() => {
    if (myExams.length === 0) return [];
    return myExams.map((exam) => {
      const scores = exam.results?.map((r) => toNumber(r.score)) || [];
      const avg = scores.length ? scores.reduce((a, b) => a + b, 0) / scores.length : 0;
      const maxScore = exam.questions?.reduce((s, q) => s + toNumber(q.score, 1), 0) || 1;
      const percentage = maxScore > 0 ? Math.round((avg / maxScore) * 100) : 0;
      return {
        name: exam.title.substring(0, 6),
        score: percentage,
        rawScore: avg,
        maxScore,
        color: ['#4f46e5', '#8b5cf6', '#10b981', '#f59e0b', '#0ea5e9', '#ec4899'][myExams.indexOf(exam) % 6],
      };
    });
  }, [myExams]);

  /* ============ Filters ============ */
  const matchesStatusFilter = useCallback((exam, filter) => {
    if (filter === 'همه') return true;
    const s = getExamDisplayStatus(exam);
    if (filter === 'فعال') return s.type === 'active';
    if (filter === 'در انتظار' || filter === 'در انتظار شروع') return s.type === 'pending';
    if (filter === 'بسته شده') return s.type === 'closed';
    if (filter === 'بایگانی شده') return s.type === 'archived';
    if (filter === 'نیاز به تصحیح' || filter === 'در انتظار تصحیح') {
      const hasEssay = (exam.questions || []).some((q) => q.type === 'essay' || q.type === 'file');
      return hasEssay && exam.results?.some((r) => r.isEssayGraded === false);
    }
    return exam.status === filter;
  }, [getExamDisplayStatus]);

  /* 🆕 گزینه‌های پویا برای فیلترهای «آزمون‌های کلاس من» */
  const myAvailableGrades = useMemo(() => {
    const s = new Set();
    myExams.forEach((e) => { if (e.grade) s.add(e.grade); });
    return [...s].sort();
  }, [myExams]);

  const myAvailableBooks = useMemo(() => {
    const s = new Set();
    myExams
      .filter((e) => !myGradeFilter || e.grade === myGradeFilter)
      .forEach((e) => { if (e.book) s.add(e.book); });
    return [...s].sort();
  }, [myExams, myGradeFilter]);

  const myAvailableChapters = useMemo(() => {
    const s = new Set();
    myExams
      .filter((e) => !myGradeFilter || e.grade === myGradeFilter)
      .filter((e) => !myBookFilter || e.book === myBookFilter)
      .forEach((e) => { if (e.chapter) s.add(e.chapter); });
    return [...s].sort((a, b) => {
      const na = parseInt(a.replace(/\D/g, ''), 10) || 0;
      const nb = parseInt(b.replace(/\D/g, ''), 10) || 0;
      return na - nb || a.localeCompare(b);
    });
  }, [myExams, myGradeFilter, myBookFilter]);

  const hasMyExamsFilters = Boolean(
    searchTerm || selectedStatusFilter !== 'همه' ||
    myGradeFilter || myBookFilter || myChapterFilter
  );

  const resetMyExamsFilters = () => {
    setSearchTerm('');
    setSelectedStatusFilter('همه');
    setMyGradeFilter('');
    setMyBookFilter('');
    setMyChapterFilter('');
  };

  const filteredExams = useMemo(
    () => myExams.filter((e) => {
      const m = e.title?.includes(searchTerm) || e.book?.includes(searchTerm);
      if (!m) return false;
      if (!matchesStatusFilter(e, selectedStatusFilter)) return false;
      if (myGradeFilter && e.grade !== myGradeFilter) return false;
      if (myBookFilter && e.book !== myBookFilter) return false;
      if (myChapterFilter && e.chapter !== myChapterFilter) return false;
      return true;
    }),
    [myExams, searchTerm, selectedStatusFilter, myGradeFilter, myBookFilter, myChapterFilter, matchesStatusFilter]
  );
  const filteredGlobalExams = useMemo(
    () => globalExams.filter((e) => {
      const m = e.title?.includes(searchTerm) || e.book?.includes(searchTerm) || e.grade?.includes(searchTerm) || e.subject?.includes(searchTerm);
      return m && matchesStatusFilter(e, selectedStatusFilter);
    }),
    [globalExams, searchTerm, selectedStatusFilter, matchesStatusFilter]
  );
  const filteredChallengeExams = useMemo(
    () => challengeExams.filter((e) => {
      const m = e.title?.includes(searchTerm) || e.book?.includes(searchTerm) || e.grade?.includes(searchTerm) || e.subject?.includes(searchTerm);
      return m && matchesStatusFilter(e, selectedStatusFilter);
    }),
    [challengeExams, searchTerm, selectedStatusFilter, matchesStatusFilter]
  );

  /* 🆕 Management Modal — منبع و فیلترهای پویا */
  const managementSourceExams = useMemo(() => {
    if (managementModalOpen === 'global') return globalExams;
    if (managementModalOpen === 'challenge') return challengeExams;
    return [];
  }, [managementModalOpen, globalExams, challengeExams]);

  const mgmtAvailableGrades = useMemo(() => {
    const s = new Set();
    managementSourceExams.forEach((e) => { if (e.grade) s.add(e.grade); });
    return [...s].sort();
  }, [managementSourceExams]);

  const mgmtAvailableFields = useMemo(() => {
    const s = new Set();
    managementSourceExams
      .filter((e) => !mgmtGrade || e.grade === mgmtGrade)
      .forEach((e) => { if (e.field) s.add(e.field); });
    return [...s];
  }, [managementSourceExams, mgmtGrade]);

  const mgmtAvailableBooks = useMemo(() => {
    const s = new Set();
    managementSourceExams
      .filter((e) => !mgmtGrade || e.grade === mgmtGrade)
      .filter((e) => !mgmtField || e.field === mgmtField)
      .forEach((e) => { if (e.book) s.add(e.book); });
    return [...s].sort();
  }, [managementSourceExams, mgmtGrade, mgmtField]);

  const mgmtAvailableChapters = useMemo(() => {
    const s = new Set();
    managementSourceExams
      .filter((e) => !mgmtGrade || e.grade === mgmtGrade)
      .filter((e) => !mgmtField || e.field === mgmtField)
      .filter((e) => !mgmtBook || e.book === mgmtBook)
      .forEach((e) => { if (e.chapter) s.add(e.chapter); });
    return [...s].sort((a, b) => {
      const na = parseInt(a.replace(/\D/g, ''), 10) || 0;
      const nb = parseInt(b.replace(/\D/g, ''), 10) || 0;
      return na - nb || a.localeCompare(b);
    });
  }, [managementSourceExams, mgmtGrade, mgmtField, mgmtBook]);

  const mgmtFilteredExams = useMemo(() => {
    const q = mgmtSearchTerm.trim().toLowerCase();
    return managementSourceExams.filter((e) => {
      if (mgmtGrade && e.grade !== mgmtGrade) return false;
      if (mgmtField && e.field !== mgmtField) return false;
      if (mgmtBook && e.book !== mgmtBook) return false;
      if (mgmtChapter && e.chapter !== mgmtChapter) return false;
      if (mgmtStatusFilter !== 'همه' && !matchesStatusFilter(e, mgmtStatusFilter)) return false;
      if (q) {
        const hay = [e.title, e.book, e.subject, e.chapter, e.grade, e.fieldTitle]
          .filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [managementSourceExams, mgmtGrade, mgmtField, mgmtBook, mgmtChapter, mgmtStatusFilter, mgmtSearchTerm, matchesStatusFilter]);

  const mgmtVisibleExams = useMemo(() => {
    return mgmtFilteredExams.slice(0, mgmtVisibleCount);
  }, [mgmtFilteredExams, mgmtVisibleCount]);

  // 🆕 ریست صفحه‌بندی هر بار فیلتر/جستجو تغییر کند
  useEffect(() => {
    if (managementModalOpen) setMgmtVisibleCount(24);
  }, [mgmtGrade, mgmtField, mgmtBook, mgmtChapter, mgmtStatusFilter, mgmtSearchTerm, managementModalOpen]);

  /* ============ Handlers ============ */
  const goToCreateExam = () => navigate('/exams/create');
  const goToCreateGlobalExam = () => navigate('/exams/create?mode=global');
  const goToCreateChallengeExam = () => navigate('/exams/create?mode=challenge');
  const goToEditExam = (examId) => navigate(`/exams/create/${examId}`);
  const goToCreateAI = () => navigate('/exams/create-ai');

  // 🆕 Bottom sheet انتخاب نوع آزمون (فقط ادمین)
  const openCreateTypeSheet = (kind) => setCreateTypeSheet(kind);
  const closeCreateTypeSheet = () => setCreateTypeSheet(null);

  const goToCreateWithChoice = (kind, method) => {
    const params = new URLSearchParams();
    if (kind === 'public') params.set('mode', 'global');
    if (kind === 'challenge') params.set('mode', 'challenge');
    const qs = params.toString();
    const base = method === 'ai' ? '/exams/create-ai' : '/exams/create';
    navigate(qs ? `${base}?${qs}` : base);
    setCreateTypeSheet(null);
  };

  // 🆕 Management Modal — باز/بسته کردن با ریست فیلترها
  const openManagementModal = (type) => {
    setManagementModalOpen(type);
    setMgmtGrade('');
    setMgmtField('');
    setMgmtBook('');
    setMgmtChapter('');
    setMgmtStatusFilter('همه');
    setMgmtSearchTerm('');
    setMgmtVisibleCount(24);
  };
  const closeManagementModal = () => setManagementModalOpen(null);

  const handleExamSelectForStatus = (examId) => {
    if (!examId) { setSelectedExamForStatus(null); return; }
    const exam = myExams.find((e) => e.id === examId);
    setSelectedExamForStatus(exam || null);
  };

  const handleDeleteClick = (examId) => { setSelectedExamId(examId); setShowDeleteModal(true); };
  const confirmDelete = () => {
    if (selectedExamId) {
      const ok = deleteExam(selectedExamId);
      if (ok) { setShowDeleteModal(false); setSelectedExamId(null); alert('🗑️ آزمون با موفقیت حذف شد'); }
    }
  };
  const cancelDelete = () => { setShowDeleteModal(false); setSelectedExamId(null); };

  const handleDuplicate = (examId) => { if (duplicateExam(examId)) alert('✅ آزمون با موفقیت کپی شد!'); };

  // 🆕 فعال‌سازی سریع آزمون ذخیره‌موقت (pending → active)
  const handlePublishExam = async (exam) => {
    if (!exam) return;
    if (exam.statusType !== 'pending') {
      alert('این آزمون قبلاً منتشر شده است.');
      return;
    }

    // اعتبارسنجی: مجموع نمرات باید دقیقاً ۲۰ باشد
    const maxScore = (exam.questions || []).reduce(
      (s, q) => s + toNumber(q.score, 0),
      0
    );
    if (Math.abs(maxScore - 20) > 0.01) {
      alert(`⚠️ برای فعال‌سازی، مجموع نمرات باید دقیقاً ۲۰ باشد.\nمجموع فعلی: ${maxScore} از ۲۰\n\nلطفاً ابتدا آزمون را ویرایش کنید.`);
      return;
    }

    // اعتبارسنجی: حداقل یک سوال
    if (!exam.questions || exam.questions.length === 0) {
      alert('⚠️ این آزمون هیچ سوالی ندارد. لطفاً ابتدا سوال اضافه کنید.');
      return;
    }

    const msg = `آیا می‌خواهید آزمون «${exam.title}» را فعال و برای دانش‌آموزان منتشر کنید؟`;
    const ok = typeof window.askConfirm === 'function'
      ? await window.askConfirm(msg)
      : window.confirm(msg);
    if (!ok) return;

    const result = updateExam(exam.id, {
      published: true,
      status: 'فعال',
      statusType: 'active',
      updatedAt: new Date().toISOString(),
    });

    if (result) alert('✅ آزمون با موفقیت فعال و منتشر شد');
    else alert('❌ خطا در فعال‌سازی آزمون');
  };

  const handleArchiveClick = (examId) => { setSelectedExamForArchive(examId); setShowArchiveModal(true); };
  const confirmArchive = () => {
    if (selectedExamForArchive) {
      const r = archiveExam(selectedExamForArchive);
      if (r) { setShowArchiveModal(false); setSelectedExamForArchive(null); alert('📦 آزمون بایگانی شد'); }
    }
  };
  const cancelArchive = () => { setShowArchiveModal(false); setSelectedExamForArchive(null); };

  const handleRestore = async (examId) => {
    const msg = 'آیا از بازیابی این آزمون مطمئن هستید؟';
    const ok = typeof window.askConfirm === 'function' ? await window.askConfirm(msg) : window.confirm(msg);
    if (ok) { const r = restoreExam(examId); if (r) alert('✅ آزمون بازیابی شد'); }
  };

  const handleShowResults = (exam) => {
    if (!exam) { alert('آزمون یافت نشد.'); return; }
    if (!exam.results || exam.results.length === 0) { alert('هیچ نتیجه‌ای برای این آزمون وجود ندارد.'); return; }
    setSelectedExamForResults(exam); setShowResultsModal(true);
  };
  const closeResultsModal = () => { setShowResultsModal(false); setSelectedExamForResults(null); };

  const handleCorrection = useCallback((exam) => {
    if (!exam) return;
    const participants = exam.results || [];
    if (participants.length === 0) { alert('هیچ دانش‌آموزی در این آزمون شرکت نکرده است.'); return; }
    const essayQs = (exam.questions || []).filter((q) => q.type === 'essay' || q.type === 'file');
    if (essayQs.length === 0) { alert('⚠️ این آزمون سوال تشریحی یا فایلی ندارد.'); return; }
    const hasAnyAnswer = participants.some((p) => p.answers && p.answers.length > 0);
    if (!hasAnyAnswer) { alert('⚠️ هیچ دانش‌آموزی پاسخی برای سوالات تشریحی ارسال نکرده است.'); return; }
    setSelectedExamForCorrection(exam);
    setCurrentCorrectionIndex(0);
    setCorrectionScores({});
    setCorrectionError(null);
    setShowCorrectionModal(true);
  }, []);

  const handleStudentCorrection = useCallback((sid, qIdx, ok) => {
    setCorrectionScores((prev) => ({ ...prev, [`${sid}-${qIdx}`]: ok }));
  }, []);

  const saveCorrection = useCallback(() => {
    if (isSavingCorrection) return;
    setIsSavingCorrection(true);
    setCorrectionError(null);
    try {
      const exam = selectedExamForCorrection;
      if (!exam) throw new Error('آزمون یافت نشد.');
      const student = exam.results[currentCorrectionIndex];
      if (!student) throw new Error('دانش‌آموزی برای تصحیح وجود ندارد.');
      const essayQs = (exam.questions || []).map((q, i) => ({ ...q, index: i })).filter((q) => q.type === 'essay' || q.type === 'file');
      const corrections = Object.keys(correctionScores).map((key) => {
        const dash = key.lastIndexOf('-');
        return { questionIndex: parseInt(key.slice(dash + 1), 10), isCorrect: correctionScores[key] };
      });
      if (corrections.length !== essayQs.length) {
        alert('⚠️ لطفاً همه‌ی سوالات تشریحی را تصحیح کنید.');
        setIsSavingCorrection(false);
        return;
      }
      const result = processCorrection(exam.id, student.studentId, corrections);
      if (!result) throw new Error('خطا در ذخیره‌ی تصحیح');
      const updated = result.exam;
      if (currentCorrectionIndex < updated.results.length - 1) {
        setCurrentCorrectionIndex((p) => p + 1);
        setCorrectionScores({});
        setSelectedExamForCorrection(updated);
      } else {
        setShowCorrectionModal(false);
        setSelectedExamForCorrection(null);
        setCorrectionScores({});
        setShowSuccessModal(true);
      }
    } catch (err) {
      console.error('Error in saveCorrection:', err);
      setCorrectionError(err.message);
      alert(err.message || 'خطا در ذخیره تصحیح');
    } finally { setIsSavingCorrection(false); }
  }, [isSavingCorrection, selectedExamForCorrection, currentCorrectionIndex, correctionScores, processCorrection]);

  const closeCorrectionModal = () => {
    if (isSavingCorrection) return;
    setShowCorrectionModal(false);
    setSelectedExamForCorrection(null);
    setCorrectionScores({});
    setCorrectionError(null);
  };

  const calculateCurrentScore = useCallback(() => {
    if (!selectedExamForCorrection || !selectedExamForCorrection.results) return 0;
    const exam = selectedExamForCorrection;
    const student = exam.results[currentCorrectionIndex];
    if (!student) return 0;
    let score = 0;
    (exam.questions || []).forEach((q, idx) => {
      const isEssay = q.type === 'essay' || q.type === 'file';
      const key = `${student.studentId}-${idx}`;
      const c = correctionScores[key];
      const qs = toNumber(q.score, 1);
      if (isEssay) {
        if (c === true) score += qs;
        else if (c === false) {}
        else {
          const a = student.answers?.find((x) => x.questionIndex === idx || x.questionId === q.id);
          if (a && a.isCorrect === true) score += qs;
        }
      } else {
        const a = student.answers?.find((x) => x.questionIndex === idx || x.questionId === q.id);
        if (a && a.isCorrect) score += qs;
      }
    });
    return score;
  }, [selectedExamForCorrection, currentCorrectionIndex, correctionScores]);
  const currentScore = useMemo(() => calculateCurrentScore(), [calculateCurrentScore, correctionScores, currentCorrectionIndex]);

  /* ============ Exam card renderer ============ */
  const renderExamCard = (exam, variant = 'my') => {
    const hasEssay = (exam.questions || []).some((q) => q.type === 'essay' || q.type === 'file');
    const hasParticipants = exam.results && exam.results.length > 0;
    const isArchived = exam.statusType === 'archived';
    const needsCorrection = hasEssay && exam.results?.some((r) => r.isEssayGraded === false);
    const displayStatus = getExamDisplayStatus(exam);
    const isSpecial = variant === 'global' || variant === 'challenge';

    const metas = isSpecial
      ? [
          { text: formatGradeWithField(exam) || '—' },
          { text: exam.subject || '—' },
          { text: exam.book || '—' },
          { text: exam.chapter || '—' },
          { text: `${exam.participants || 0} نفر` },
        ]
      : [
          { text: exam.book || '—' },
          { text: exam.date || '—' },
          { text: `${exam.participants || 0} از ${exam.totalStudents || 0} نفر` },
        ];

    return (
      <article key={exam.id} className="exam-card" data-status={displayStatus.type}>
        <div className="stripe" />
        <div className="exam-body">
          <div className="exam-top">
            <div className="exam-title">
              <div className="exam-title-icon">{isSpecial ? I.globe : I.file}</div>
              <span>{exam.title}</span>
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <span className={classNames('status-badge', displayStatus.type)}>{displayStatus.label}</span>
              {needsCorrection && (
                <span className="status-badge correction" title="نیاز به تصحیح">✏️ در انتظار تصحیح</span>
              )}
            </div>
          </div>
          <div className="exam-meta">
            {metas.map((m, i) => (
              <span key={i} className="exam-meta-item">
                <span className="exam-meta-value">{m.text}</span>
              </span>
            ))}
          </div>
        </div>
        <div className="exam-actions">
          {!isArchived ? (
            <>
              <button className="icon-btn primary" onClick={() => goToEditExam(exam.id)} title="ویرایش">
                {I.edit}
                <span className="icon-btn-label">ویرایش</span>
              </button>
              {displayStatus.type === 'pending' && (
                <button
                  className="icon-btn success"
                  onClick={() => handlePublishExam(exam)}
                  title="فعال‌سازی و انتشار سریع"
                >
                  {I.rocket}
                  <span className="icon-btn-label">انتشار</span>
                </button>
              )}
              <button className="icon-btn" onClick={() => handleDuplicate(exam.id)} title="کپی">
                {I.copy}
                <span className="icon-btn-label">کپی</span>
              </button>
              <button className="icon-btn success" onClick={() => handleShowResults(exam)} disabled={!hasParticipants} title="نتایج">
                {I.results}
                <span className="icon-btn-label">نتایج</span>
              </button>
              {hasEssay && (
                <button
                  className="icon-btn"
                  style={needsCorrection ? { background: 'var(--warning-soft)', color: 'var(--warning)' } : { color: 'var(--success)' }}
                  onClick={() => handleCorrection(exam)}
                  title={needsCorrection ? 'نیاز به تصحیح' : 'تصحیح'}
                >
                  {needsCorrection ? I.edit : I.check}
                  <span className="icon-btn-label">تصحیح</span>
                </button>
              )}
              <button className="icon-btn" onClick={() => handleArchiveClick(exam.id)} title="بایگانی">
                {I.archive}
                <span className="icon-btn-label">بایگانی</span>
              </button>
              <button className="icon-btn danger" onClick={() => handleDeleteClick(exam.id)} title="حذف">
                {I.trash}
                <span className="icon-btn-label">حذف</span>
              </button>
            </>
          ) : (
            <>
              <button className="icon-btn" onClick={() => handleRestore(exam.id)} title="بازیابی">
                {I.restore}
                <span className="icon-btn-label">بازیابی</span>
              </button>
              <button className="icon-btn danger" onClick={() => handleDeleteClick(exam.id)} title="حذف">
                {I.trash}
                <span className="icon-btn-label">حذف</span>
              </button>
            </>
          )}
        </div>
      </article>
    );
  };

  /* ============ Tabs renders ============ */
  const renderMyExamsPanel = () => (
    <section className="tab-panel active">
      <div className="card">
        <div className="card-header">
          <div className="card-title">{I.file} لیست آزمون‌ها</div>
          <div className="filters">
            <input
              type="text" className="search-input" placeholder="جستجوی آزمون..."
              value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            />
            <select
              className="select-input" value={selectedStatusFilter}
              onChange={(e) => setSelectedStatusFilter(e.target.value)}
            >
              <option value="همه">همه وضعیت‌ها</option>
              <option value="فعال">فعال</option>
              <option value="در انتظار">در انتظار</option>
              <option value="بسته شده">بسته شده</option>
              <option value="نیاز به تصحیح">نیاز به تصحیح</option>
              <option value="بایگانی شده">بایگانی شده</option>
            </select>
            <button
              type="button"
              className={classNames('icon-btn my-filters-toggle', showMyExamsFilters && 'active')}
              onClick={() => setShowMyExamsFilters((v) => !v)}
              title="دسته‌بندی آزمون‌ها"
              aria-expanded={showMyExamsFilters}
            >
              {I.filter}
              <span className="my-filters-toggle-label">دسته‌بندی</span>
            </button>
          </div>
        </div>

        {showMyExamsFilters && (
          <div className="my-filters-panel">
            <div className="my-filter-item">
              <label>پایه تحصیلی</label>
              <select
                className="select-input"
                value={myGradeFilter}
                onChange={(e) => {
                  setMyGradeFilter(e.target.value);
                  setMyBookFilter('');
                  setMyChapterFilter('');
                }}
              >
                <option value="">همه پایه‌ها</option>
                {myAvailableGrades.map((g) => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
            </div>

            <div className="my-filter-item">
              <label>کتاب درسی</label>
              <select
                className="select-input"
                value={myBookFilter}
                onChange={(e) => {
                  setMyBookFilter(e.target.value);
                  setMyChapterFilter('');
                }}
              >
                <option value="">همه کتاب‌ها</option>
                {myAvailableBooks.map((b) => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
            </div>

            <div className="my-filter-item">
              <label>📖 فصل</label>
              <select
                className="select-input"
                value={myChapterFilter}
                onChange={(e) => setMyChapterFilter(e.target.value)}
                disabled={!myBookFilter || myAvailableChapters.length === 0}
              >
                <option value="">همه فصل‌ها</option>
                {myAvailableChapters.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            {hasMyExamsFilters && (
              <button
                type="button"
                className="my-filters-reset"
                onClick={resetMyExamsFilters}
              >
                ✕ پاک کردن همه فیلترها
              </button>
            )}
          </div>
        )}

        <div className="card-body" style={{ padding: 16 }}>
          <div className="exam-list">
            {filteredExams.length > 0 ? (
              filteredExams.map((e) => renderExamCard(e, 'my'))
            ) : (
              <div className="empty-state">
                <div className="empty-icon">{I.file}</div>
                <p className="empty-title">{myExams.length === 0 ? 'هنوز آزمونی نساخته‌اید' : 'آزمونی با این فیلتر یافت نشد'}</p>
                <p className="empty-desc">{myExams.length === 0 ? 'با دکمه «ساخت آزمون» اولین آزمون خود را ایجاد کنید' : 'فیلترها را تغییر دهید'}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );

  const renderStudentsPanel = () => {
    let members = [];
    try {
      const s = localStorage.getItem('teamMembers');
      if (s) members = JSON.parse(s).filter((m) => m.status === 'active');
    } catch (e) {}

    if (!selectedExamForStatus) {
      return (
        <section className="tab-panel active">
          <div className="card">
            <div className="card-header">
              <div className="card-title">{I.users} وضعیت دانش‌آموزان</div>
              <div className="filters">
                <select className="select-input" style={{ minWidth: 280 }} onChange={(e) => handleExamSelectForStatus(e.target.value)} defaultValue="">
                  <option value="">-- انتخاب آزمون --</option>
                  {myExams.map((ex) => (
                    <option key={ex.id} value={ex.id}>{ex.title} ({ex.participants || 0} نفر)</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="card-body">
              <div className="empty-state">
                <div className="empty-icon">{I.users}</div>
                <p className="empty-title">انتخاب آزمون</p>
                <p className="empty-desc">لطفاً یک آزمون از لیست بالا انتخاب کنید.</p>
              </div>
            </div>
          </div>
        </section>
      );
    }

    const exam = selectedExamForStatus;
    const participants = exam.results || [];
    const totalStudents = exam.totalStudents || getTeamTotalStudents(exam.teamId) || members.length || 0;
    const studentList = members.map((mb) => {
      const r = participants.find((p) => p.studentId === mb.studentId);
      const stats = r ? getStudentStatsForExam(exam, r) : { correct: 0, wrong: 0, unanswered: exam.questions?.length || 0, pending: 0, wrongQuestions: [] };
      return {
        name: getStudentName(mb.studentId), studentId: mb.studentId,
        score: r?.score ?? '--', correct: stats.correct, wrong: stats.wrong,
        unanswered: stats.unanswered, pending: stats.pending, wrongQuestions: stats.wrongQuestions,
        hasParticipated: !!r, isGraded: r?.isEssayGraded !== false,
      };
    });
    const sorted = [...studentList].sort((a, b) => {
      const sa = typeof a.score === 'number' ? a.score : -1;
      const sb = typeof b.score === 'number' ? b.score : -1;
      return sb - sa;
    });
    const participatedCount = sorted.filter((s) => s.hasParticipated).length;
    const maxScore = exam.questions?.reduce((sum, q) => sum + toNumber(q.score, 1), 0) || 1;
    const passThreshold = calculatePassThreshold(maxScore);
    const passedCount = sorted.filter((s) => typeof s.score === 'number' && s.score >= passThreshold).length;
    const failedCount = sorted.filter((s) => typeof s.score === 'number' && s.score < passThreshold).length;
    const absentCount = sorted.filter((s) => !s.hasParticipated).length;
    const correctionCount = sorted.filter((s) => s.hasParticipated && !s.isGraded).length;
    const avgScore = participatedCount > 0
      ? (sorted.filter((s) => s.hasParticipated).reduce((a, s) => a + (typeof s.score === 'number' ? s.score : 0), 0) / participatedCount).toFixed(1)
      : '--';

    return (
      <section className="tab-panel active">
        <div className="card">
          <div className="card-header">
            <div className="card-title">{I.users} وضعیت دانش‌آموزان</div>
            <div className="filters">
              <select
                className="select-input" style={{ minWidth: 280 }}
                value={selectedExamForStatus?.id || ''}
                onChange={(e) => handleExamSelectForStatus(e.target.value)}
              >
                <option value="">-- انتخاب آزمون --</option>
                {myExams.map((ex) => (
                  <option key={ex.id} value={ex.id}>{ex.title} ({ex.participants || 0} نفر)</option>
                ))}
              </select>
            </div>
          </div>
          <div className="card-body">
            <div className="student-toolbar">
              <span className="chip">📌 {exam.title}</span>
              {exam.field && <span className="chip">🎓 {getFieldMeta(exam.field)?.title || exam.field}</span>}
              <span className="chip">📚 {exam.book}</span>
              <span className="chip">📅 {exam.date}</span>
              <span className="chip">⏱ {exam.duration} دقیقه</span>
            </div>
            <div className="quick-stats">
              <div className="quick-stat"><span className="quick-stat-value">{sorted.length}</span><span className="quick-stat-label">کل دانش‌آموزان</span></div>
              <div className="quick-stat"><span className="quick-stat-value text-success">{participatedCount}</span><span className="quick-stat-label">شرکت‌کنندگان</span></div>
              <div className="quick-stat"><span className="quick-stat-value text-danger">{absentCount}</span><span className="quick-stat-label">غایبان</span></div>
              <div className="quick-stat"><span className="quick-stat-value">{avgScore}</span><span className="quick-stat-label">میانگین</span></div>
              <div className="quick-stat"><span className="quick-stat-value text-success">{passedCount}</span><span className="quick-stat-label">قبول</span></div>
              <div className="quick-stat"><span className="quick-stat-value text-danger">{failedCount}</span><span className="quick-stat-label">رد</span></div>
              <div className="quick-stat"><span className="quick-stat-value text-warning">{correctionCount}</span><span className="quick-stat-label">در انتظار تصحیح</span></div>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>رتبه</th><th>دانش‌آموز</th><th>نمره</th><th>درست</th><th>غلط</th><th>نزده</th><th>در انتظار</th><th>سوالات غلط</th><th>وضعیت</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.length > 0 ? sorted.map((s, idx) => {
                    const isPassed = typeof s.score === 'number' && s.score >= passThreshold;
                    const rankCls = idx === 0 && s.hasParticipated ? 'gold' : idx === 1 && s.hasParticipated ? 'silver' : idx === 2 && s.hasParticipated ? 'bronze' : '';
                    return (
                      <tr key={s.studentId} style={!s.hasParticipated ? { opacity: 0.55 } : undefined}>
                        <td>{s.hasParticipated ? <span className={classNames('rank-cell', rankCls)}>{idx + 1}</span> : <span className="rank-cell" style={{ opacity: 0.5 }}>—</span>}</td>
                        <td><div className="student-name-cell"><div className="student-avatar">{s.name.charAt(0) || '👤'}</div>{s.name}</div></td>
                        <td>{s.hasParticipated ? <span className={classNames('score-cell', isPassed ? 'pass' : 'fail')}>{s.score}</span> : '—'}</td>
                        <td className="correct-count">{s.hasParticipated ? s.correct : '—'}</td>
                        <td className="wrong-count">{s.hasParticipated ? s.wrong : '—'}</td>
                        <td>{s.hasParticipated ? s.unanswered : '—'}</td>
                        <td>{s.hasParticipated && s.pending > 0 ? <span className="pending-pill">⏳ {s.pending}</span> : '۰'}</td>
                        <td style={{ fontSize: 11, color: 'var(--text-muted)' }}>{s.hasParticipated && s.wrongQuestions?.length > 0 ? s.wrongQuestions.join('، ') : '—'}</td>
                        <td>
                          {!s.hasParticipated ? <span className="status-pill danger">غایب</span>
                            : !s.isGraded ? <span className="status-pill warning">در انتظار تصحیح</span>
                            : <span className="status-pill success">انجام شده</span>}
                        </td>
                      </tr>
                    );
                  }) : (
                    <tr><td colSpan="9" style={{ textAlign: 'center', padding: 40 }}>
                      <div className="empty-icon" style={{ margin: '0 auto 12px' }}>{I.users}</div>
                      <p className="empty-title">هیچ دانش‌آموزی در گروه وجود ندارد.</p>
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>
    );
  };

  const renderReportsPanel = () => (
    <section className="tab-panel active">
      <div className="card">
        <div className="card-header">
          <div className="card-title">{I.barChart} گزارشات و آمار</div>
          {/*<div className="filters">
            <button className="btn btn-outline" onClick={() => alert('📥 خروجی PDF (در حال توسعه)')}>خروجی PDF</button>
            <button className="btn btn-outline" onClick={() => alert('📥 خروجی Excel (در حال توسعه)')}>خروجی Excel</button>
          </div>*/}
        </div>
        <div className="card-body">
          <div className="report-grid">
            {reports.map((r, i) => (
              <div key={i} className="report-card">
                <div className={classNames('report-icon', r.tone)}>{r.icon}</div>
                <div className="report-value">{r.value}</div>
                <div className="report-label">{r.label}</div>
                <div className="report-sub">{r.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="card">
        <div className="card-header">
          <div className="card-title">نمودار عملکرد آزمون‌ها</div>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>درصد میانگین</span>
        </div>
        <div className="card-body">
          {chartData.length > 0 ? (
            <>
              <div className="chart-wrap">
                {chartData.map((c, i) => {
                  const h = c.score > 0 ? c.score * 2 : 4;
                  return (
                    <div key={i} className="bar-wrap">
                      <span className="bar-value">{c.score}%</span>
                      <div
                        className="bar"
                        style={{
                          height: `${h}px`,
                          background: h > 4 ? `linear-gradient(180deg, ${c.color}, ${c.color}cc)` : 'var(--border)',
                        }}
                        title={`${c.score}% (${c.rawScore.toFixed(1)}/${c.maxScore})`}
                      />
                      <span className="bar-label">{c.name}</span>
                    </div>
                  );
                })}
              </div>
              <div className="chart-legend">
                <span>میانگین کل: {chartData.length ? (chartData.reduce((a, b) => a + b.score, 0) / chartData.length).toFixed(1) + '%' : '—'}</span>
                <span>بالاترین: {chartData.length ? Math.max(...chartData.map((d) => d.score)) + '%' : '—'}</span>
                <span>پایین‌ترین: {chartData.length ? Math.min(...chartData.map((d) => d.score)) + '%' : '—'}</span>
              </div>
            </>
          ) : (
            <div className="empty-state"><p className="empty-title">هیچ داده‌ای برای نمایش وجود ندارد</p></div>
          )}
        </div>
      </div>
    </section>
  );

  const renderManagementPanel = () => (
    <section className="tab-panel active">
      <div className="card">
        <div className="card-header">
          <div className="card-title">{I.globe} مدیریت آزمون‌های عمومی</div>
          <div className="filters">
            <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>{globalStats.total} آزمون | {globalStats.active} فعال</span>
            <button className="btn btn-outline" onClick={() => openManagementModal('global')}>
              📂 نمایش
            </button>
          </div>
        </div>
      </div>
      <div className="card">
        <div className="card-header">
          <div className="card-title">{I.trophy} مدیریت چالش‌ها</div>
          <div className="filters">
            <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>{challengeStats.total} چالش | {challengeStats.active} فعال</span>
            <button className="btn btn-outline" onClick={() => openManagementModal('challenge')}>
              📂 نمایش
            </button>
          </div>
        </div>
      </div>
    </section>
  );

  /* ============ Modals ============ */
  const renderDeleteModal = () => {
    if (!showDeleteModal) return null;
    const exam = myExams.find((e) => e.id === selectedExamId)
      || globalExams.find((e) => e.id === selectedExamId)
      || challengeExams.find((e) => e.id === selectedExamId);
    if (!exam) return null;
    return (
      <div className="modal open" onClick={cancelDelete}>
        <div className="modal-content" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
          <div className="modal-header"><div className="modal-title">حذف آزمون</div><button className="modal-close" onClick={cancelDelete}>✕</button></div>
          <div className="modal-body">
            <p style={{ marginBottom: 12, color: 'var(--text-secondary)' }}>آیا از حذف آزمون <strong style={{ color: 'var(--text)' }}>"{exam.title}"</strong> مطمئن هستید؟</p>
            <div style={{ padding: '12px 16px', background: 'var(--danger-soft)', borderRight: '3px solid var(--danger)', borderRadius: 'var(--r-sm)', fontSize: 12, color: 'var(--danger)', fontWeight: 600 }}>این عمل غیرقابل بازگشت است و تمام داده‌های آزمون حذف می‌شود.</div>
          </div>
          <div className="modal-footer"><button className="btn btn-outline" onClick={cancelDelete}>انصراف</button><button className="btn btn-danger" onClick={confirmDelete}>حذف شود</button></div>
        </div>
      </div>
    );
  };

  const renderArchiveModal = () => {
    if (!showArchiveModal) return null;
    const exam = myExams.find((e) => e.id === selectedExamForArchive)
      || globalExams.find((e) => e.id === selectedExamForArchive)
      || challengeExams.find((e) => e.id === selectedExamForArchive);
    if (!exam) return null;
    return (
      <div className="modal open" onClick={cancelArchive}>
        <div className="modal-content" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
          <div className="modal-header"><div className="modal-title">بایگانی آزمون</div><button className="modal-close" onClick={cancelArchive}>✕</button></div>
          <div className="modal-body">
            <p style={{ marginBottom: 12, color: 'var(--text-secondary)' }}>آیا از بایگانی کردن آزمون <strong style={{ color: 'var(--text)' }}>"{exam.title}"</strong> مطمئن هستید؟</p>
            <div style={{ padding: '12px 16px', background: 'var(--bg-subtle)', borderRight: '3px solid var(--text-muted)', borderRadius: 'var(--r-sm)', fontSize: 12, color: 'var(--text-secondary)' }}>آزمون بایگانی شده از لیست فعال حذف می‌شود و قابل بازیابی است.</div>
          </div>
          <div className="modal-footer"><button className="btn btn-outline" onClick={cancelArchive}>انصراف</button><button className="btn btn-primary" onClick={confirmArchive}>بایگانی</button></div>
        </div>
      </div>
    );
  };

  const renderResultsModal = () => {
    if (!selectedExamForResults) return null;
    const exam = selectedExamForResults;
    const participants = exam.results || [];
    const absent = Math.max(0, (exam.totalStudents || 0) - participants.length);
    const maxScore = exam.questions?.reduce((s, q) => s + toNumber(q.score, 1), 0) || 1;
    const passThreshold = calculatePassThreshold(maxScore);
    return (
      <div className="modal open" onClick={closeResultsModal}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header"><div className="modal-title">نتایج آزمون: {exam.title}</div><button className="modal-close" onClick={closeResultsModal}>✕</button></div>
          <div className="modal-body">
            {exam.field && (
              <div style={{ marginBottom: 12, fontSize: 13, color: 'var(--text-secondary)' }}>
                🎓 رشته: <strong>{getFieldMeta(exam.field)?.title || exam.field}</strong> • پایه: <strong>{exam.grade}</strong>
              </div>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, padding: '12px 16px', background: 'var(--bg-subtle)', borderRadius: 'var(--r-sm)', marginBottom: 16, fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>
              <span>👥 شرکت‌کنندگان: {participants.length} نفر</span>
              <span>❌ غایبان: {absent} نفر</span>
              <span>📊 میانگین: {participants.length ? (participants.reduce((a, b) => a + toNumber(b.score), 0) / participants.length).toFixed(1) : '—'}</span>
              <span>🎯 نمره قبولی: {passThreshold}</span>
            </div>
            <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)' }}>
              <table className="data-table">
                <thead>
                  <tr><th>ردیف</th><th>دانش‌آموز</th><th>نمره</th><th>درست</th><th>غلط</th><th>نزده</th><th>در انتظار</th><th>سوالات غلط</th></tr>
                </thead>
                <tbody>
                  {participants.map((s, idx) => {
                    const st = getStudentStatsForExam(exam, s);
                    const isPassed = toNumber(s.score) >= passThreshold;
                    return (
                      <tr key={idx}>
                        <td>{idx + 1}</td>
                        <td>{getStudentName(s.studentId)}</td>
                        <td><span className={classNames('score-cell', isPassed ? 'pass' : 'fail')}>{s.score}</span></td>
                        <td className="correct-count">{st.correct}</td>
                        <td className="wrong-count">{st.wrong}</td>
                        <td>{st.unanswered}</td>
                        <td>{st.pending > 0 ? <span className="pending-pill">⏳ {st.pending}</span> : '۰'}</td>
                        <td style={{ fontSize: 11, color: 'var(--text-muted)' }}>{st.wrongQuestions.join('، ') || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {absent > 0 && (
              <div style={{ marginTop: 16, padding: '12px 16px', background: 'var(--bg-subtle)', borderRadius: 'var(--r-sm)' }}>
                <h4 style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 8 }}>❌ دانش‌آموزان غایب:</h4>
                <span style={{ padding: '3px 10px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 999, fontSize: 11.5, fontWeight: 600 }}>{absent} نفر غایب</span>
              </div>
            )}
          </div>
          <div className="modal-footer"><button className="btn btn-outline" onClick={closeResultsModal}>بستن</button></div>
        </div>
      </div>
    );
  };

  const renderCorrectionModal = () => {
    if (!selectedExamForCorrection) return null;
    const exam = selectedExamForCorrection;
    const participants = exam.results || [];
    const student = participants[currentCorrectionIndex];
    if (!student) return null;
    const essayQs = (exam.questions || []).map((q, i) => ({ ...q, index: i })).filter((q) => q.type === 'essay' || q.type === 'file');
    const answers = student.answers || [];
    const hasAnswers = answers.length > 0;
    const studentName = getStudentName(student.studentId);

    return (
      <div className="modal open" onClick={closeCorrectionModal}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <div className="modal-title">تصحیح آزمون: {exam.title}</div>
            <button className="modal-close" onClick={closeCorrectionModal} disabled={isSavingCorrection}>✕</button>
          </div>
          <div className="modal-body">
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-secondary)', fontWeight: 600, marginBottom: 6 }}>
                <span>تصحیح دانش‌آموز {currentCorrectionIndex + 1} از {participants.length}</span>
                <span>{Math.round(((currentCorrectionIndex + 1) / participants.length) * 100)}٪</span>
              </div>
              <div style={{ width: '100%', height: 6, background: 'var(--bg-subtle)', borderRadius: 999, overflow: 'hidden' }}>
                <div style={{ width: `${((currentCorrectionIndex + 1) / participants.length) * 100}%`, height: '100%', background: 'linear-gradient(90deg, var(--brand), var(--violet))', transition: 'width .4s var(--ease)' }} />
              </div>
            </div>

            <div style={{ padding: '14px 16px', background: 'var(--bg-subtle)', borderRadius: 'var(--r-sm)', marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div className="student-avatar" style={{ width: 42, height: 42, fontSize: 16 }}>{studentName.charAt(0) || '👤'}</div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 800 }}>{studentName}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>شناسه: {student.studentId}</div>
                </div>
              </div>
              <div style={{ marginTop: 10, display: 'inline-flex', alignItems: 'center', padding: '4px 12px', background: 'var(--brand-soft)', color: 'var(--brand)', borderRadius: 999, fontSize: 12, fontWeight: 700 }}>نمره فعلی: {currentScore}</div>
            </div>

            {correctionError && (
              <div style={{ padding: '12px 16px', marginBottom: 12, background: 'var(--danger-soft)', color: 'var(--danger)', borderRadius: 'var(--r-sm)', borderRight: '3px solid var(--danger)', fontSize: 12.5, fontWeight: 600 }}>❌ {correctionError}</div>
            )}

            {!hasAnswers ? (
              <div style={{ padding: 16, background: 'var(--warning-soft)', borderRight: '3px solid var(--warning)', borderRadius: 'var(--r-sm)', display: 'flex', gap: 12 }}>
                <span style={{ fontSize: 24 }}>⚠️</span>
                <div>
                  <p style={{ fontSize: 13, fontWeight: 700 }}>این دانش‌آموز هنوز پاسخی ثبت نکرده است.</p>
                  <p style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 4 }}>دانش‌آموز ممکن است آزمون را شروع نکرده باشد.</p>
                </div>
              </div>
            ) : essayQs.length > 0 ? (
              essayQs.map((q, qIdx) => {
                const a = answers.find((x) => x.questionIndex === q.index || x.questionId === q.id);
                const userAnswer = a?.text || 'پاسخی ثبت نشده';
                const key = `${student.studentId}-${q.index}`;
                const isCorrect = correctionScores[key] !== undefined ? correctionScores[key] : (a?.isCorrect !== undefined ? a.isCorrect : null);
                return (
                  <div key={qIdx} style={{ padding: 16, border: '1px solid var(--border)', borderRadius: 'var(--r-md)', marginBottom: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, paddingBottom: 10, borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 13, fontWeight: 800 }}>سوال {qIdx + 1}</span>
                      <span style={{ padding: '2px 10px', background: 'var(--bg-subtle)', color: 'var(--text-secondary)', borderRadius: 999, fontSize: 11, fontWeight: 600 }}>نمره: {toNumber(q.score, 1)}</span>
                      <span style={{ padding: '2px 10px', background: 'var(--bg-subtle)', color: 'var(--text-secondary)', borderRadius: 999, fontSize: 11, fontWeight: 600 }}>تشریحی</span>
                    </div>
                    <div style={{ fontSize: 13, lineHeight: 1.9, marginBottom: 12 }} dangerouslySetInnerHTML={{ __html: sanitizeHtml(q.question) }} />
                    {q.mathFormula && <div style={{ padding: '8px 12px', background: 'var(--warning-soft)', borderRight: '3px solid var(--warning)', borderRadius: 6, fontSize: 12.5, fontWeight: 600, marginBottom: 12 }}>📐 فرمول: {q.mathFormula}</div>}
                    <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 }}>📝 پاسخ دانش‌آموز:</div>
                    <div style={{ padding: '10px 14px', background: 'var(--bg-subtle)', borderRadius: 'var(--r-sm)', fontSize: 13, lineHeight: 1.9, minHeight: 60 }}>
                      {userAnswer === 'پاسخی ثبت نشده' ? <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>{userAnswer}</span> : userAnswer}
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                      <button
                        className={classNames('btn', isCorrect === true ? 'btn-success' : 'btn-outline')}
                        onClick={() => handleStudentCorrection(student.studentId, q.index, true)}
                        disabled={isSavingCorrection}
                        style={{ borderColor: isCorrect === true ? 'var(--success)' : 'var(--success)', color: isCorrect === true ? '#fff' : 'var(--success)' }}
                      >✅ صحیح</button>
                      <button
                        className={classNames('btn', isCorrect === false ? 'btn-danger' : 'btn-outline')}
                        onClick={() => handleStudentCorrection(student.studentId, q.index, false)}
                        disabled={isSavingCorrection}
                        style={{ borderColor: isCorrect === false ? 'var(--danger)' : 'var(--danger)', color: isCorrect === false ? '#fff' : 'var(--danger)' }}
                      >❌ غلط</button>
                    </div>
                  </div>
                );
              })
            ) : (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>ℹ️ این آزمون سوال تشریحی ندارد.</div>
            )}

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', justifyContent: 'space-between', marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', gap: 12, fontSize: 12, color: 'var(--text-secondary)' }}>
                <span>✅ تصحیح شده: {Object.keys(correctionScores).length} از {essayQs.length}</span>
                <span>📊 نمره فعلی: <strong>{currentScore}</strong></span>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                {!hasAnswers ? (
                  <button
                    className="btn btn-outline"
                    style={{ borderColor: 'var(--warning)', color: 'var(--warning)' }}
                    onClick={() => {
                      if (currentCorrectionIndex < participants.length - 1) {
                        setCurrentCorrectionIndex((p) => p + 1);
                        setCorrectionScores({});
                        setCorrectionError(null);
                      } else {
                        alert('✅ همه دانش‌آموزان بررسی شدند!');
                        setShowCorrectionModal(false);
                        setSelectedExamForCorrection(null);
                        setCorrectionScores({});
                        setCorrectionError(null);
                      }
                    }}
                    disabled={isSavingCorrection}
                  >⏭️ رد شدن</button>
                ) : (
                  <button
                    className="btn btn-primary"
                    onClick={saveCorrection}
                    disabled={isSavingCorrection || Object.keys(correctionScores).length !== essayQs.length}
                  >
                    {isSavingCorrection ? '⏳ در حال ذخیره...' : currentCorrectionIndex < participants.length - 1 ? '💾 ثبت و نفر بعد' : '✅ ثبت و پایان'}
                  </button>
                )}
                {hasAnswers && Object.keys(correctionScores).length !== essayQs.length && (
                  <span style={{ padding: '4px 10px', background: 'var(--warning-soft)', color: 'var(--warning)', borderRadius: 999, fontSize: 11, fontWeight: 600 }}>⚠️ همه سوالات را تصحیح کنید</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSuccessModal = () => {
    if (!showSuccessModal) return null;
    return (
      <div className="modal open" onClick={() => setShowSuccessModal(false)}>
        <div className="modal-content" style={{ maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
          <div className="modal-body" style={{ textAlign: 'center', padding: '32px 24px' }}>
            <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'var(--success-soft)', color: 'var(--success)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            </div>
            <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--success)', marginBottom: 8 }}>تصحیح با موفقیت انجام شد!</h2>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.9, marginBottom: 20 }}>
              تمام دانش‌آموزان این آزمون تصحیح شدند.<br />نمرات نهایی محاسبه و ذخیره گردید.
            </p>
            <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => { setShowSuccessModal(false); loadTeamMembers(); }}>بستن</button>
          </div>
        </div>
      </div>
    );
  };







  /* 🆕 Management Modal: فیلتر حرفه‌ای آزمون‌های عمومی و چالش */
  const renderManagementModal = () => {
    if (!managementModalOpen) return null;

    const isGlobal = managementModalOpen === 'global';
    const title = isGlobal ? '🌍 مدیریت آزمون‌های عمومی' : '🏆 مدیریت چالش‌ها';
    const variant = isGlobal ? 'global' : 'challenge';
    const totalCount = managementSourceExams.length;
    const filteredCount = mgmtFilteredExams.length;
    const remaining = filteredCount - mgmtVisibleCount;
    const hasMore = remaining > 0;

    return (
      <div className="modal open" onClick={closeManagementModal}>
        <div
          className="modal-content mgmt-modal-content"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-header">
            <div className="modal-title">{title}</div>
            <button className="modal-close" onClick={closeManagementModal}>✕</button>
          </div>

          <div className="modal-body mgmt-modal-body">
            {/* ===== ناحیه کنترل (ثابت) ===== */}
            <div className="mgmt-controls">
              {/* جستجو */}
              <div className="mgmt-search-wrap">
                <span className="mgmt-search-icon">🔍</span>
                <input
                  type="text"
                  className="mgmt-search-input"
                  placeholder="جستجو در نام آزمون، کتاب، درس، فصل..."
                  value={mgmtSearchTerm}
                  onChange={(e) => setMgmtSearchTerm(e.target.value)}
                />
                {mgmtSearchTerm && (
                  <button
                    type="button"
                    className="mgmt-search-clear"
                    onClick={() => setMgmtSearchTerm('')}
                    title="پاک کردن"
                  >✕</button>
                )}
              </div>

              {/* فیلترها */}
              <div className="mgmt-filters-grid">
                <div className="mgmt-filter-item">
                  <label>پایه تحصیلی</label>
                  <select
                    className="select-input"
                    value={mgmtGrade}
                    onChange={(e) => {
                      setMgmtGrade(e.target.value);
                      setMgmtField('');
                      setMgmtBook('');
                      setMgmtChapter('');
                    }}
                  >
                    <option value="">همه پایه‌ها</option>
                    {mgmtAvailableGrades.map((g) => (
                      <option key={g} value={g}>{g}</option>
                    ))}
                  </select>
                </div>

                {mgmtAvailableFields.length > 0 && (
                  <div className="mgmt-filter-item">
                    <label>رشته تحصیلی</label>
                    <select
                      className="select-input"
                      value={mgmtField}
                      onChange={(e) => {
                        setMgmtField(e.target.value);
                        setMgmtBook('');
                        setMgmtChapter('');
                      }}
                    >
                      <option value="">همه رشته‌ها</option>
                      {mgmtAvailableFields.map((f) => (
                        <option key={f} value={f}>
                          {getFieldMeta(f)?.title || f}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="mgmt-filter-item">
                  <label>کتاب درسی</label>
                  <select
                    className="select-input"
                    value={mgmtBook}
                    onChange={(e) => {
                      setMgmtBook(e.target.value);
                      setMgmtChapter('');
                    }}
                  >
                    <option value="">همه کتاب‌ها</option>
                    {mgmtAvailableBooks.map((b) => (
                      <option key={b} value={b}>{b}</option>
                    ))}
                  </select>
                </div>

                {mgmtBook && mgmtAvailableChapters.length > 0 && (
                  <div className="mgmt-filter-item">
                    <label>📖 فصل</label>
                    <select
                      className="select-input"
                      value={mgmtChapter}
                      onChange={(e) => setMgmtChapter(e.target.value)}
                    >
                      <option value="">همه فصل‌ها</option>
                      {mgmtAvailableChapters.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="mgmt-filter-item">
                  <label>🎯 وضعیت</label>
                  <select
                    className="select-input"
                    value={mgmtStatusFilter}
                    onChange={(e) => setMgmtStatusFilter(e.target.value)}
                  >
                    <option value="همه">همه وضعیت‌ها</option>
                    <option value="فعال">فعال</option>
                    <option value="در انتظار">در انتظار</option>
                    <option value="بسته شده">بسته شده</option>
                    <option value="نیاز به تصحیح">نیاز به تصحیح</option>
                  </select>
                </div>
              </div>

              {/* خلاصه */}
              <div className="mgmt-results-summary">
                <span>
                  📊 {filteredCount === totalCount
                    ? `${totalCount} آزمون`
                    : `${filteredCount} از ${totalCount} آزمون`}
                </span>
                <span className="mgmt-visible-info">
                  نمایش {mgmtVisibleExams.length}
                </span>
              </div>
            </div>

            {/* ===== ناحیه نتایج (اسکرول مستقل) ===== */}
            <div className="mgmt-results-scroll">
              {filteredCount > 0 ? (
                <>
                  {mgmtVisibleExams.map((e) => renderExamCard(e, variant))}
                  {hasMore && (
                    <button
                      type="button"
                      className="mgmt-load-more-btn"
                      onClick={() => setMgmtVisibleCount((c) => c + 24)}
                    >
                      ⬇️ بارگذاری بیشتر — {remaining} آزمون دیگر
                    </button>
                  )}
                </>
              ) : (
                <div className="empty-state">
                  <div className="empty-icon">{isGlobal ? I.globe : I.trophy}</div>
                  <p className="empty-title">
                    {mgmtSearchTerm
                      ? `آزمونی با "${mgmtSearchTerm}" پیدا نشد`
                      : 'آزمونی با این فیلترها پیدا نشد'}
                  </p>
                  <p className="empty-desc">
                    {mgmtSearchTerm
                      ? 'جستجو را پاک کنید یا عبارت دیگری امتحان کنید'
                      : 'فیلترها را تغییر دهید'}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="modal-footer">
            <button className="btn btn-outline" onClick={closeManagementModal}>بستن</button>
          </div>
        </div>
      </div>
    );
  };









  /* 🆕 Bottom Sheet: انتخاب روش ساخت آزمون (فقط ادمین) */
  const renderCreateTypeSheet = () => {
    if (!createTypeSheet) return null;

    const kindLabel =
      createTypeSheet === 'group' ? 'گروهی' :
      createTypeSheet === 'public' ? 'عمومی' : 'چالش';

    return (
      <div className="create-type-sheet" onClick={closeCreateTypeSheet}>
        <div className="create-type-sheet-content" onClick={(e) => e.stopPropagation()}>
          <div className="create-type-sheet-handle" />

          <div className="create-type-sheet-header">
            <h3>ساخت آزمون {kindLabel}</h3>
            <p>روش ساخت را انتخاب کنید</p>
          </div>

          <div className="create-type-sheet-options">
            <button
              className="create-type-option ai"
              onClick={() => goToCreateWithChoice(createTypeSheet, 'ai')}
            >
              <span className="option-icon">✨</span>
              <div className="option-body">
                <span className="option-title">ساخت با هوش مصنوعی</span>
                <span className="option-desc">فقط موضوع رو بگو، بقیه رو AI می‌سازه</span>
              </div>
            </button>

            <button
              className="create-type-option manual"
              onClick={() => goToCreateWithChoice(createTypeSheet, 'manual')}
            >
              <span className="option-icon">✏️</span>
              <div className="option-body">
                <span className="option-title">ساخت دستی</span>
                <span className="option-desc">سوال‌ها رو خودت طراحی کن</span>
              </div>
            </button>
          </div>

          <button className="create-type-cancel" onClick={closeCreateTypeSheet}>
            انصراف
          </button>
        </div>
      </div>
    );
  };

  /* ============ Login required ============ */
  if (!teacherId) {
    return (
      <div className="exams-teacher-container" data-theme={darkMode ? 'dark' : 'light'}>
        <main className="container">
          <div className="card" style={{ maxWidth: 480, margin: '60px auto', textAlign: 'center', padding: '60px 32px' }}>
            <div style={{ fontSize: 64, marginBottom: 12 }}>🔒</div>
            <h2 style={{ fontSize: 22, fontWeight: 800, marginBottom: 8 }}>لطفاً ابتدا وارد شوید</h2>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20 }}>برای مدیریت آزمون‌ها باید به عنوان معلم وارد حساب خود شوید.</p>
            <button className="btn btn-primary" onClick={() => navigate('/login')}>🚀 ورود</button>
          </div>
        </main>
      </div>
    );
  }

  /* ============ Main ============ */
  const initial = (teacherName && teacherName.trim().charAt(0)) || 'م';
  const tabs = [
    { key: 'my-exams', label: 'آزمون‌های کلاس من', icon: I.file, badge: myExams.length },
    { key: 'students', label: 'وضعیت دانش‌آموزان من', icon: I.users },
    { key: 'reports', label: 'گزارشات کلاس من', icon: I.barChart },
  ];
  if (isAdmin) tabs.push({ key: 'management', label: 'مدیریت کل', icon: I.settings, badge: 'ادمین', adminBadge: true });

  return (
    <div className="exams-teacher-container" data-theme={darkMode ? 'dark' : 'light'}>
      <header className="topbar">
        <div className="brand">
          <div className="brand-icon">{I.file}</div>
          <span>مدیریت آزمون‌ها</span>
          
        </div>
        <div className="topbar-actions">
          {/*<button className="icon-btn" onClick={() => setDarkMode((v) => !v)} title={darkMode ? 'تم روشن' : 'تم تیره'}>
            {darkMode ? I.sun : I.moon}
          </button>*/}
          <div className="user-chip">
            <div className="user-avatar">{initial}</div>
            <span>{teacherName}</span>
            {isAdmin && <span className="role-badge">ادمین</span>}
          </div>
        </div>
      </header>

      <main className="container">
        <div className="page-header">
          <div>
            <h1 className="page-title">مدیریت آزمون‌ها</h1>
            <p className="page-subtitle">ایجاد، مدیریت و تحلیل آزمون‌های گروهی، عمومی و چالش‌ها</p>
          </div>
        </div>


        <section className="kpis">
          {kpiData.map((k, i) => (
            <div key={i} className="kpi">
              <div className={classNames('kpi-icon', k.tone)}>{k.icon}</div>
              <div>
                <div className="kpi-value">{k.value}</div>
                <div className="kpi-label">{k.label}</div>
              </div>
            </div>
          ))}
        </section>

        <section className="launchpad">
          <div className="launchpad-label">ساخت آزمون جدید :</div>

          {isAdmin ? (
            // 🆕 مدیر سیستم: ۳ گزینه — نوع آزمون
            <div className="launchpad-grid admin create-type-grid">
              <button className="action-btn primary" onClick={() => openCreateTypeSheet('group')}>
                {I.file}
                <div className="btn-body">
                  <span>آزمون گروهی</span>
                  <span className="desc">فقط برای گروه شما</span>
                </div>
              </button>
              <button className="action-btn" onClick={() => openCreateTypeSheet('public')}>
                {I.globe}
                <div className="btn-body">
                  <span>آزمون عمومی</span>
                  <span className="desc">برای همه دانش‌آموزان</span>
                </div>
              </button>
              <button className="action-btn" onClick={() => openCreateTypeSheet('challenge')}>
                {I.trophy}
                <div className="btn-body">
                  <span>چالش جدید</span>
                  <span className="desc">با جوایز سکه و XP</span>
                </div>
              </button>
            </div>
          ) : (
            // معلم عادی: همان ۲ دکمه قبلی — دست‌نخورده
            <div className="launchpad-grid">
              <button className="action-btn primary" onClick={goToCreateAI}>
                {I.sparkles}
                <div className="btn-body">
                  <span>ساخت آزمون با هوش مصنوعی</span>
                  <span className="desc">سریع‌ترین راه — فقط موضوع رو بگو</span>
                </div>
              </button>
              <button className="action-btn" onClick={goToCreateExam}>
                {I.pencil}
                <span>ساخت دستی</span>
              </button>
            </div>
          )}
        </section>

        <nav className="tabs">
          {tabs.map((t) => (
            <button
              key={t.key}
              className={classNames('tab', activeTab === t.key && 'active')}
              onClick={() => setActiveTab(t.key)}
            >
              {t.icon}
              {t.label}
              {t.badge !== undefined && <span className="tab-badge">{t.badge}</span>}
            </button>
          ))}
        </nav>

        {activeTab === 'my-exams' && renderMyExamsPanel()}
        {activeTab === 'students' && renderStudentsPanel()}
        {activeTab === 'reports' && renderReportsPanel()}
        {isAdmin && activeTab === 'management' && renderManagementPanel()}
      </main>

      {renderDeleteModal()}
      {renderArchiveModal()}
      {renderResultsModal()}
      {renderCorrectionModal()}
      {renderSuccessModal()}
      {renderCreateTypeSheet()}
      {renderManagementModal()}
    </div>
  );
};

export default ExamsTeacher;